import { useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TodoItemResponse, TodoProjectResponse } from '@/lib/api';
import { TodoItemRow } from './TodoItemRow';
import { useSectionCollapse } from '../lib/collapsedSections';
import { resolveProjectColor } from '../lib/projectColors';

const NO_PROJECT = '__none__';

interface ProjectGroupedViewProps {
  /** Active, already-filtered top-level items. */
  items: TodoItemResponse[];
  projects: TodoProjectResponse[];
  canEditItem: (item: TodoItemResponse) => boolean;
  isManager: boolean;
}

/** One collapsible project group (read-only — a grouping lens, not a drop target). */
function ProjectGroup({
  groupKey,
  label,
  color,
  items,
  canEditItem,
  isManager,
}: {
  groupKey: string;
  label: string;
  color: string | null;
  items: TodoItemResponse[];
  canEditItem: (item: TodoItemResponse) => boolean;
  isManager: boolean;
}) {
  const { expanded, toggle } = useSectionCollapse(`project:${groupKey}`);
  const dot = resolveProjectColor(color);

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-1.5 py-1 text-left hover:text-primary"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} project "${label}"`}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-shrink-0" />
        )}
        {groupKey !== NO_PROJECT && (
          <span className={cn('h-2.5 w-2.5 flex-shrink-0 rounded-full', dot.dot)} />
        )}
        <span className="truncate text-sm font-medium text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground/60">{items.length}</span>
      </button>

      {expanded && (
        <div className="mt-1">
          {items.map((item) => (
            <TodoItemRow
              key={item.itemId}
              item={item}
              canEdit={canEditItem(item)}
              canManageSection={isManager}
              groupContext="project"
              dragDisabled
            />
          ))}
          {items.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground/70">No tasks in this project.</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The list grouped by PROJECT instead of section — the same tasks under a
 * different lens. Every project (plus a "No project" bucket) is a collapsible
 * group. This is a READ-ONLY grouping view: rows aren't draggable — reordering
 * lives in the Sections view, and a task's project is changed from its edit
 * dialog.
 */
export function ProjectGroupedView({
  items,
  projects,
  canEditItem,
  isManager,
}: ProjectGroupedViewProps) {
  const itemsByProject = useMemo(() => {
    const map = new Map<string, TodoItemResponse[]>();
    map.set(NO_PROJECT, []);
    for (const p of projects) map.set(p.projectId, []);
    for (const item of items) {
      const key = item.projectId && map.has(item.projectId) ? item.projectId : NO_PROJECT;
      map.get(key)!.push(item);
    }
    for (const list of map.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [items, projects]);

  // Order: "No project" first, then projects in their sort order.
  const groups: Array<{ key: string; label: string; color: string | null }> = [
    { key: NO_PROJECT, label: 'No project', color: null },
    ...projects.map((p) => ({ key: p.projectId, label: p.name, color: p.color })),
  ];

  const hasAny = items.length > 0;

  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <ProjectGroup
          key={g.key}
          groupKey={g.key}
          label={g.label}
          color={g.color}
          items={itemsByProject.get(g.key) ?? []}
          canEditItem={canEditItem}
          isManager={isManager}
        />
      ))}
      {!hasAny && (
        <div className="py-12 text-center text-muted-foreground">
          <p>No tasks yet. Add tasks and assign them to a project.</p>
        </div>
      )}
    </div>
  );
}
